/**
 * My Timesheets Page
 * View and manage personal timesheets
 */

import React, { useState } from 'react';
import { message } from 'antd';
import { TimesheetHeader } from '@/components/features/timesheet/TimesheetHeader';
import { DailyView } from '@/components/features/timesheet/DailyView';
import { WeeklyView } from '@/components/features/timesheet/WeeklyView';
import { MonthlyView } from '@/components/features/timesheet/MonthlyView';
import { YearlyView } from '@/components/features/timesheet/YearlyView';
import {
  useExportTimesheet,
  useInvalidateTimesheetQueries,
} from '@/hooks/useTimesheet';
import type { TimesheetView } from '@/api/types/timesheet.types';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(isoWeek);

export const MyTimesheetsPage: React.FC = () => {
  const today = dayjs();
  const [view, setView] = useState<TimesheetView>('monthly');
  const [year, setYear] = useState(today.year());
  const [month, setMonth] = useState(today.month() + 1); // dayjs month is 0-indexed
  const [week, setWeek] = useState(today.isoWeek());
  const [date, setDate] = useState(today.format('YYYY-MM-DD'));
  const [showAll, setShowAll] = useState(true);

  const exportMutation = useExportTimesheet();
  const { invalidateAll } = useInvalidateTimesheetQueries();

  const handlePeriodChange = (newYear: number, newMonth: number) => {
    setYear(newYear);
    setMonth(newMonth);
  };

  const handleWeekChange = (newYear: number, newWeek: number) => {
    setYear(newYear);
    setWeek(newWeek);
  };

  const handleDateChange = (newDate: string) => {
    setDate(newDate);
    // Also update year/month/week based on new date
    const d = dayjs(newDate);
    setYear(d.year());
    setMonth(d.month() + 1);
    setWeek(d.isoWeek());
  };

  const handleYearChange = (newYear: number) => {
    setYear(newYear);
  };

  const handleExport = async () => {
    try {
      await exportMutation.mutateAsync({
        period: view,
        year,
        month: view === 'monthly' ? month : undefined,
        week: view === 'weekly' ? week : undefined,
        date: view === 'daily' ? date : undefined,
        format: 'csv',
      });
      message.success('Timesheet exported successfully');
    } catch (error) {
      message.error('Failed to export timesheet');
    }
  };

  const handleRefresh = () => {
    invalidateAll();
    message.success('Timesheet data refreshed');
  };

  const renderView = () => {
    switch (view) {
      case 'daily':
        return <DailyView date={date} showAll={showAll} canEdit={true} />;
      case 'weekly':
        return (
          <WeeklyView
            year={year}
            week={week}
            showAll={showAll}
            canEdit={true}
          />
        );
      case 'monthly':
        return (
          <MonthlyView
            year={year}
            month={month}
            showAll={showAll}
            canEdit={true} // User can edit their own timesheets
          />
        );
      case 'yearly':
        return <YearlyView year={year} showAll={showAll} canEdit={true} />;
      default:
        return null;
    }
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <TimesheetHeader
        view={view}
        onViewChange={setView}
        year={year}
        month={month}
        week={week}
        date={date}
        onPeriodChange={handlePeriodChange}
        onWeekChange={handleWeekChange}
        onDateChange={handleDateChange}
        onYearChange={handleYearChange}
        showAll={showAll}
        onShowAllChange={setShowAll}
        onExport={handleExport}
        onRefresh={handleRefresh}
      />
      <div style={{ flex: 1, overflow: 'auto' }}>{renderView()}</div>
    </div>
  );
};
